import { useEffect, useMemo, useState } from 'react';
import { api2, ApiError, type LibraryItem } from '../api.ts';

/**
 * The playbooks, written for the work. Search is plain matching and is
 * deliberately not dressed up as anything cleverer.
 */
export default function Toolkit(): React.ReactElement {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api2
      .library()
      .then((res) => setItems(res.items))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load the toolkit.'))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => [...new Set(items.map((i) => i.category))].sort(), [items]);

  const shown = items.filter((item) => {
    if (category && item.category !== category) return false;
    if (!query.trim()) return true;
    const needle = query.toLowerCase();
    return (
      item.title.toLowerCase().includes(needle) ||
      item.blurb.toLowerCase().includes(needle) ||
      item.category.toLowerCase().includes(needle)
    );
  });

  if (loading) return <p className="muted">Loading…</p>;

  return (
    <div className="stack-lg">
      <header>
        <h1>Toolkit</h1>
        <p className="soft" style={{ marginTop: 6 }}>
          Where a poster actually works, what to say at a booth, and the email that gets a reply.
        </p>
      </header>

      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}

      <div className="field">
        <label htmlFor="lib-search">Search</label>
        <input
          id="lib-search"
          type="search"
          value={query}
          placeholder="posters, press, booth"
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {categories.length > 1 && (
        <div className="chips" role="group" aria-label="Filter by kind">
          <button className="chip" aria-pressed={category === null} onClick={() => setCategory(null)}>
            Everything
          </button>
          {categories.map((c) => (
            <button key={c} className="chip" aria-pressed={category === c} onClick={() => setCategory(c)}>
              {c}
            </button>
          ))}
        </div>
      )}

      <section className="stack">
        {shown.length === 0 ? (
          <div className="card empty">
            <p>Nothing matches that.</p>
          </div>
        ) : (
          shown.map((item) => (
            <article key={item.id} className="card stack" style={{ gap: 10 }}>
              <div className="row" style={{ gap: 8 }}>
                <span className="pill indigo">{item.category}</span>
                {item.format && <span className="pill">{item.format}</span>}
                {item.sharedBy && <span className="pill mint">Shared by {item.sharedBy}</span>}
              </div>
              <div>
                <h2>{item.title}</h2>
                <p className="soft small" style={{ marginTop: 6 }}>
                  {item.blurb}
                </p>
              </div>

              {openId === item.id && item.body.length > 0 && (
                <div className="stack" style={{ gap: 6, background: 'var(--paper)', padding: 14, borderRadius: 14 }}>
                  <span className="kicker">The short version</span>
                  {item.body.map((line, i) => (
                    <p key={i} className="small soft">
                      {line}
                    </p>
                  ))}
                </div>
              )}

              <div className="row">
                <button className="btn quiet" onClick={() => setOpenId(openId === item.id ? null : item.id)}>
                  {openId === item.id ? 'Close' : 'Preview'}
                </button>
              </div>
            </article>
          ))
        )}
      </section>
    </div>
  );
}
